import React from "react";
import { Box, Typography, Skeleton, useTheme } from "@mui/material";
import { Card } from "@/components/molecules/Card";
import { useRenderPerformance } from "@/components/utils/performance";
import { ProductCard } from "../ProductCard/ProductCard";
import { CategorySectionProps } from "./CategorySection.types";

/**
 * Category Section Component
 */
export const CategorySection: React.FC<CategorySectionProps> = React.memo(
  ({ category, products, onProductSelect, onAddToCart, loading = false }) => {
    useRenderPerformance("CategorySection");

    const theme = useTheme();

    if (products.length === 0 && !loading) {
      return null;
    }

    return (
      <Box
        px={{
          xs: 2,
          md: 3,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: 26,
            fontWeight: "bold",
            lineHeight: 1.1,
            py: 2,
          }}
        >
          {category.name}
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)", // 2 карточки в ряд
              sm: "repeat(3, 1fr)", // 3 карточки в ряд
              md: "repeat(4, 1fr)", // 4 карточки в ряд
              lg: "repeat(5, 1fr)", // 5 карточек в ряд
              xl: "repeat(6, 1fr)", // 6 карточек в ряд
            },
            gap: {
              xs: 1.5, // 12px
              sm: 1.75, // 14px
              md: 2, // 16px
              lg: 2, // 16px
              xl: 2.25, // 18px
            },
          }}
        >
          {loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <Card key={index} sx={{ height: "100%" }}>
                  <Skeleton
                    variant="rectangular"
                    sx={{
                      width: "100%",
                      aspectRatio: "1",
                      borderRadius: 3,
                    }}
                  />
                  <Box sx={{ p: theme.spacing(2) }}>
                    <Skeleton variant="text" height={24} />
                    <Skeleton variant="text" height={16} />
                    <Skeleton
                      variant="rectangular"
                      height={theme.spacing(4)} // 32px
                      sx={{
                        mt: theme.spacing(2), // 16px
                        borderRadius: theme.spacing(1.25), // 10px
                      }}
                    />
                  </Box>
                </Card>
              ))
            : products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onClick={onProductSelect}
                  onAddToCart={onAddToCart}
                />
              ))}
        </Box>
      </Box>
    );
  }
);

CategorySection.displayName = "CategorySection";
